import {
  Briefcase,
  ChevronRight,
  Clock,
  FileText,
  Home,
  Plus,
  Search,
  Settings,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Dialog } from '@/components/catalyst/dialog';
import { Input } from '@/components/catalyst/input';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router';
import { useStore } from '@/store';

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  category: 'navigation' | 'action' | 'recent' | 'workspace';
  action: () => void;
  keywords?: string[];
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const isOpen = useStore((state) => state.isCommandPaletteOpen);
  const setOpen = useStore((state) => state.setCommandPaletteOpen);
  const workspaces = useStore((state) => state.workspaces);
  const currentWorkspace = useStore((state) => state.currentWorkspace);
  const switchWorkspace = useStore((state) => state.switchWorkspace);

  // Command items
  const commands = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [
      // Navigation
      {
        id: 'nav-dashboard',
        title: 'Go to Dashboard',
        icon: Home,
        category: 'navigation',
        action: () => {
          navigate('/dashboard');
          setOpen(false);
        },
        keywords: ['home', 'overview'],
      },
      {
        id: 'nav-submissions',
        title: 'View Submissions',
        icon: FileText,
        category: 'navigation',
        action: () => {
          navigate('/submissions');
          setOpen(false);
        },
        keywords: ['email', 'submissions', 'checks'],
      },
      {
        id: 'nav-workspaces',
        title: 'Manage Workspaces',
        icon: Briefcase,
        category: 'navigation',
        action: () => {
          navigate('/workspaces');
          setOpen(false);
        },
        keywords: ['organization', 'team'],
      },
      {
        id: 'nav-settings',
        title: 'Open Settings',
        icon: Settings,
        category: 'navigation',
        action: () => {
          navigate('/settings');
          setOpen(false);
        },
        keywords: ['preferences', 'config'],
      },
      // Actions
      // Removed new campaign action for MVP
      {
        id: 'action-new-workspace',
        title: 'Create New Workspace',
        description: 'Set up a new workspace',
        icon: Plus,
        category: 'action',
        action: () => {
          navigate('/workspaces/new');
          setOpen(false);
        },
        keywords: ['new', 'add', 'team'],
      },
    ];

    // Add workspace switching commands
    const workspacesList = Array.isArray(workspaces) ? workspaces : [];
    workspacesList.forEach((workspace) => {
      if (workspace.id !== currentWorkspace?.id) {
        items.push({
          id: `workspace-${workspace.id}`,
          title: `Switch to ${workspace.name}`,
          description: workspace.description,
          icon: Briefcase,
          category: 'workspace',
          action: async () => {
            await switchWorkspace(workspace.id);
            setOpen(false);
          },
          keywords: ['switch', 'workspace', workspace.name.toLowerCase()],
        });
      }
    });

    // Add recent items from localStorage
    const recentItems = getRecentItems();
    recentItems.forEach((item) => {
      items.push({
        id: `recent-${item.id}`,
        title: item.title,
        description: `Recently visited`,
        icon: Clock,
        category: 'recent',
        action: () => {
          navigate(item.path);
          setOpen(false);
        },
        keywords: [],
      });
    });

    return items;
  }, [workspaces, currentWorkspace, navigate, setOpen, switchWorkspace]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query) return commands;

    const searchTerms = query.toLowerCase().split(' ');

    return commands.filter((command) => {
      const searchableText = [command.title, command.description, ...(command.keywords || [])]
        .join(' ')
        .toLowerCase();

      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [commands, query]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {
      action: [],
      navigation: [],
      workspace: [],
      recent: [],
    };

    filteredCommands.forEach((command) => {
      if (groups[command.category]) {
        groups[command.category].push(command);
      }
    });

    return groups;
  }, [filteredCommands]);

  // Keyboard navigation
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const flatCommands = filteredCommands;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % flatCommands.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + flatCommands.length) % flatCommands.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (flatCommands[selectedIndex]) {
            flatCommands[selectedIndex].action();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [filteredCommands, selectedIndex, setOpen]
  );

  // Global keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [setOpen]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={() => setOpen(false)}>
      <div className="relative">
        {/* Search Input */}
        <div className="flex items-center border-b border-zinc-200 px-4 dark:border-zinc-700">
          <Search className="h-5 w-5 flex-shrink-0 text-zinc-400" />
          <Input
            type="text"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 border-0 px-3 py-4 text-base focus:ring-0"
            autoFocus
          />
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
              No results found for "{query}"
            </div>
          ) : (
            <>
              {/* Actions */}
              {groupedCommands.action.length > 0 && (
                <CommandGroup
                  title="Actions"
                  commands={groupedCommands.action}
                  selectedIndex={selectedIndex}
                  onSelect={(command) => command.action()}
                  getCommandIndex={(command) => filteredCommands.indexOf(command)}
                />
              )}

              {/* Navigation */}
              {groupedCommands.navigation.length > 0 && (
                <CommandGroup
                  title="Navigation"
                  commands={groupedCommands.navigation}
                  selectedIndex={selectedIndex}
                  onSelect={(command) => command.action()}
                  getCommandIndex={(command) => filteredCommands.indexOf(command)}
                />
              )}

              {/* Workspaces */}
              {groupedCommands.workspace.length > 0 && (
                <CommandGroup
                  title="Switch Workspace"
                  commands={groupedCommands.workspace}
                  selectedIndex={selectedIndex}
                  onSelect={(command) => command.action()}
                  getCommandIndex={(command) => filteredCommands.indexOf(command)}
                />
              )}

              {/* Recent */}
              {groupedCommands.recent.length > 0 && (
                <CommandGroup
                  title="Recent"
                  commands={groupedCommands.recent}
                  selectedIndex={selectedIndex}
                  onSelect={(command) => command.action()}
                  getCommandIndex={(command) => filteredCommands.indexOf(command)}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-200 px-4 py-2 dark:border-zinc-700">
          <div className="flex items-center justify-center gap-4 text-xs text-zinc-400">
            <span className="flex items-center">
              <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">⌘</kbd>
              <kbd className="ml-0.5 rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">K</kbd>
              <span className="ml-1">to open</span>
            </span>
            <span className="flex items-center">
              <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 dark:bg-zinc-800">esc</kbd>
              <span className="ml-1">to close</span>
            </span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

interface CommandGroupProps {
  title: string;
  commands: CommandItem[];
  selectedIndex: number;
  onSelect: (command: CommandItem) => void;
  getCommandIndex: (command: CommandItem) => number;
}

function CommandGroup({
  title,
  commands,
  selectedIndex,
  onSelect,
  getCommandIndex,
}: CommandGroupProps) {
  return (
    <div className="mb-2">
      <div className="px-2 py-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400">
        {title}
      </div>
      {commands.map((command) => {
        const index = getCommandIndex(command);
        const isSelected = index === selectedIndex;
        const Icon = command.icon;

        return (
          <button
            key={command.id}
            onClick={() => onSelect(command)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
              isSelected
                ? 'bg-zinc-100 dark:bg-zinc-800'
                : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50'
            )}
          >
            <Icon className="h-4 w-4 flex-shrink-0 text-zinc-400" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {command.title}
              </div>
              {command.description && (
                <div className="truncate text-xs text-zinc-500 dark:text-zinc-400">
                  {command.description}
                </div>
              )}
            </div>
            {isSelected && <ChevronRight className="h-4 w-4 text-zinc-400" />}
          </button>
        );
      })}
    </div>
  );
}

// Helper function to get recent items from localStorage
function getRecentItems(): { id: string; title: string; path: string }[] {
  try {
    const stored = localStorage.getItem('command-palette-recent');
    if (!stored) return [];

    const items = JSON.parse(stored);
    return items.slice(0, 5); // Return only the 5 most recent
  } catch {
    return [];
  }
}

// Helper function to save recent item to localStorage
export function saveRecentItem(title: string, path: string) {
  try {
    const stored = localStorage.getItem('command-palette-recent');
    const items = stored ? JSON.parse(stored) : [];

    // Remove duplicates
    const filtered = items.filter((item: any) => item.path !== path);

    // Add new item at the beginning
    filtered.unshift({
      id: Date.now().toString(),
      title,
      path,
    });

    // Keep only 10 most recent
    localStorage.setItem('command-palette-recent', JSON.stringify(filtered.slice(0, 10)));
  } catch {
    // Ignore errors
  }
}
